/**
 * Click-to-edit boxes on ICS-213RR (mirrors ICS-204 editable sections).
 * Box 4 uses per-row edit; signature / auto boxes are excluded.
 */
export const ICS213RR_EDITABLE_SECTIONS = [
  'dateTime',
  'requestNumber',
  'supplySources',
  'reslCheckboxes',
  'logisticsOrder',
  'supplier',
  'logisticsNotes',
  'orderPlacedBy',
  'financeComments',
] as const;

export type Ics213RrEditingSection = (typeof ICS213RR_EDITABLE_SECTIONS)[number];

/** Boxes 10, 11, 12, and 14 — the Logistics Section filled while box 13 is open to sign. */
export const ICS213RR_LOGISTICS_SECTIONS = [
  'logisticsOrder',
  'supplier',
  'logisticsNotes',
  'orderPlacedBy',
] as const satisfies readonly Ics213RrEditingSection[];

export type Ics213RrLogisticsSection =
  (typeof ICS213RR_LOGISTICS_SECTIONS)[number];

export function isIcs213RrLogisticsSection(
  section: Ics213RrEditingSection
): section is Ics213RrLogisticsSection {
  return (ICS213RR_LOGISTICS_SECTIONS as readonly string[]).includes(section);
}

/** Tip slot id for box 9 — RESL Review / Signature. */
export const ICS213RR_RESL_SIGN_SLOT_ID = 'reslReview';

/** Tip slot id for box 13 — Logistics Section Approval. */
export const ICS213RR_LOGISTICS_SIGN_SLOT_ID = 'logisticsApproval';

/** Tip slot id for box 16 — Finance Section Approval. */
export const ICS213RR_FINANCE_SIGN_SLOT_ID = 'financeApproval';

/**
 * Whether a printed box may be click-edited.
 *
 * Logistics boxes stay editable while box 13 is open to sign, even after earlier
 * boxes have locked the tip. Draft (unsigned) logistics edits still require the
 * Logistics Section Chief seat.
 *
 * Finance box 15 stays editable while box 16 is open to sign; otherwise draft
 * edits require the Finance seat and an unlocked tip.
 *
 * RESL box 8 stays editable while box 9 is open to sign; otherwise draft edits
 * require the Resource Unit Leader seat and an unlocked tip.
 */
export function canEditIcs213RrSection(args: {
  section: Ics213RrEditingSection;
  readOnly: boolean;
  tipLocked: boolean;
  openSignSlotId: string | null | undefined;
  isResl: boolean;
  isLogs: boolean;
  isFinance: boolean;
}): boolean {
  if (args.readOnly) return false;

  if (isIcs213RrLogisticsSection(args.section)) {
    if (args.openSignSlotId === ICS213RR_LOGISTICS_SIGN_SLOT_ID) return true;
    return !args.tipLocked && args.isLogs;
  }

  if (args.section === 'financeComments') {
    if (args.openSignSlotId === ICS213RR_FINANCE_SIGN_SLOT_ID) return true;
    return !args.tipLocked && args.isFinance;
  }

  if (args.section === 'reslCheckboxes') {
    if (args.openSignSlotId === ICS213RR_RESL_SIGN_SLOT_ID) return true;
    return !args.tipLocked && args.isResl;
  }

  if (args.tipLocked) return false;
  return true;
}

/** Per-row Order # / ETA / Cost (LSC) while Logistics is the open sign slot. */
export function canEditIcs213RrLogisticsOrderCols(args: {
  readOnly: boolean;
  openSignSlotId: string | null | undefined;
}): boolean {
  return (
    !args.readOnly && args.openSignSlotId === ICS213RR_LOGISTICS_SIGN_SLOT_ID
  );
}